export const M6_UNIT_7 = {
  id: "m6-u7-present-perfect-intro",
  title: "Unidad 7: Around the House - Present Perfect",
  topic: "Learn Present Perfect! Talk about what you've JUST done around the house. Connect past actions to the present moment. Essential for daily life conversations!",
  iconType: "home",
  grammarImage: "/grammar/grammar-m6-u7.jpg",
  grammarText: "Present Perfect = HAVE/HAS + past participle. Use it for recent actions (I've just cleaned) or life experiences. Different from past simple! Key for natural English.",
  sets: [

    // ===================================================================
    // SET 1: INTRODUCING PRESENT PERFECT with JUST (10 preguntas)
    // ===================================================================
    {
      id: "m6-u7-s1",
      title: "Set 1: I've Just Done It!",
      questions: [
        {
          type: "mc",
          q: "You JUST finished cleaning. Right now, the kitchen is clean. What do you say?",
          options: ["I've just cleaned the kitchen.", "I cleaned the kitchen.", "I clean the kitchen."],
          a: "I've just cleaned the kitchen.",
          tip: "JUST finished = I've just + past participle (recent action!)",
          successtip: "✓PRESENT PERFECT!\nI've just... = acabo de... 🎉\nConnects past action to NOW!\nVery common in daily life!"
        },
        {
          type: "order",
          q: "Context: Acabas de lavar los platos justo ahora. Traduce: Acabo de lavar los platos.",
          words: ["I've", "just", "washed", "the", "dishes."],
          a: "I've just washed the dishes.",
          tip: "I'VE = I have (contraction). JUST = acabo de",
          successtip: "✓WASH THE DISHES!\n'Wash the dishes' = lavar los platos 🍽️\nHousehold chore vocabulary!"
        },
        {
          type: "match",
          q: "Match present perfect to meaning",
          pairs: [
            { left: "I've just cleaned", right: "Acabo de limpiar (now it's clean)" },
            { left: "I cleaned", right: "Limpié (past, no connection to now)" },
            { left: "I'm cleaning", right: "Estoy limpiando (doing it now)" }
          ],
          a: "completed",
          successtip: "✓THREE TIMES!\nPresent Perfect = past with present result ✓\nPast Simple = finished past ✓\nPresent Continuous = happening now ✓"
        },
        {
          type: "input",
          q: "You just finished vacuuming. The floor is clean now. I ___ just vacuumed.",
          a: "'ve",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["have", "'ve"] },
          successtip: "✓VACUUM!\n'Vacuum' = aspirar 🧹\nVacuum the carpet/floor/room"
        },
        {
          type: "mc",
          q: "FORM: I have just + ___",
          options: ["past participle (cleaned/washed/done)", "base verb (clean/wash/do)", "verb + ing (cleaning/washing)"],
          a: "past participle (cleaned/washed/done)",
          tip: "PRESENT PERFECT = have/has + PAST PARTICIPLE"
        },
        {
          type: "order",
          q: "Context: Acabas de hacer la cama. Traduce: Acabo de hacer la cama.",
          words: ["I've", "just", "made", "the", "bed."],
          a: "I've just made the bed.",
          tip: "MADE = past participle of MAKE (irregular!)",
          successtip: "✓MAKE THE BED!\n'Make the bed' = tender la cama 🛏️\nDaily morning chore!"
        },
        {
          type: "mc",
          q: "Your mom asks: 'Did you clean your room?' You JUST finished. Reply:",
          options: ["Yes, I've just cleaned it!", "Yes, I clean it!", "Yes, I cleaned it yesterday."],
          a: "Yes, I've just cleaned it!",
          tip: "JUST finished = use PRESENT PERFECT with 'just'"
        },
        {
          type: "input",
          q: "She just took out the trash. She ___ just taken out the trash.",
          a: "has",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["'s"] },
          successtip: "✓TAKE OUT THE TRASH!\n🇺🇸 'Take out the trash' (US)\n🇬🇧 'Take out the rubbish' (UK)\nCommon household chore!"
        },
        {
          type: "order",
          q: "Context: Tu roommate acaba de sacar la basura. Traduce: Él acaba de sacar la basura.",
          words: ["He's", "just", "taken", "out", "the", "trash."],
          a: "He's just taken out the trash.",
          tip: "HE'S = he has (NOT he is here!). TAKEN = past participle"
        },
        {
          type: "mc",
          q: "Which shows action JUST happened with present result?",
          options: ["I've just mopped the floor. (It's wet now)", "I mopped the floor yesterday.", "I mop the floor every day."],
          a: "I've just mopped the floor. (It's wet now)",
          successtip: "✓MOP!\n'Mop the floor' = trapear el piso 🧽\nResult visible NOW = Present Perfect!"
        }
      ]
    },

    // ===================================================================
    // SET 2: HAVE vs HAS + MORE CHORES (10 preguntas)
    // ===================================================================
    {
      id: "m6-u7-s2",
      title: "Set 2: Have & Has Forms",
      questions: [
        {
          type: "mc",
          q: "FORM: I/You/We/They ___ cleaned. He/She/It ___ cleaned.",
          options: ["have / has", "has / have", "have / have"],
          a: "have / has",
          tip: "HAVE for I/you/we/they. HAS for he/she/it",
          successtip: "✓HAVE vs HAS!\nI/You/We/They = HAVE ✓\nHe/She/It = HAS ✓\nSame rule as 'do/does'!"
        },
        {
          type: "order",
          q: "Context: Tu hermana acaba de lavar la ropa. Traduce: Ella acaba de lavar la ropa.",
          words: ["She's", "just", "done", "the", "laundry."],
          a: "She's just done the laundry.",
          tip: "SHE'S = she has. DONE = past participle of DO",
          successtip: "✓DO THE LAUNDRY!\n'Do the laundry' = lavar la ropa 👕\n🇺🇸 'Laundry' (US)\n🇬🇧 'Washing' (UK)"
        },
        {
          type: "input",
          q: "They just finished cleaning. They ___ just cleaned the bathroom.",
          a: "have",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["'ve"] }
        },
        {
          type: "mc",
          q: "My dad ___ just cooked dinner. It smells delicious!",
          options: ["has", "have", "had"],
          a: "has",
          tip: "MY DAD = he = HAS",
          successtip: "✓COOK DINNER!\n'Cook dinner' = cocinar la cena 🍳\nDaily household task!"
        },
        {
          type: "order",
          q: "Context: Tus roommates acaban de ordenar la sala. Traduce: Ellos acaban de ordenar la sala.",
          words: ["They've", "just", "tidied", "up", "the", "living", "room."],
          a: "They've just tidied up the living room.",
          tip: "THEY'VE = they have. TIDIED = past participle of TIDY",
          successtip: "✓TIDY UP!\n'Tidy up' = ordenar 📦\nBritish phrasal verb!\nUS: 'clean up' or 'straighten up'"
        },
        {
          type: "mc",
          q: "We ___ just finished the housework. Time to relax!",
          options: ["have", "has", "had"],
          a: "have",
          tip: "WE = HAVE (not has)",
          successtip: "✓HOUSEWORK!\n'Housework' = quehaceres domésticos 🏠\nGeneral term for all chores!"
        },
        {
          type: "input",
          q: "My sister ___ just swept the floor. (she)",
          a: "has",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["'s"] },
          successtip: "✓SWEEP!\n'Sweep the floor' = barrer el piso 🧹\nBefore mopping!"
        },
        {
          type: "order",
          q: "Context: Tú y tu amigo acaban de lavar las ventanas. Traduce: Nosotros acabamos de lavar las ventanas.",
          words: ["We've", "just", "cleaned", "the", "windows."],
          a: "We've just cleaned the windows.",
          successtip: "✓CLEAN THE WINDOWS!\nClean the windows = limpiar las ventanas 🪟\nSpring cleaning task!"
        },
        {
          type: "mc",
          q: "ERROR CHECK: Which is WRONG?",
          options: ["He have just cleaned.", "He has just cleaned.", "He's just cleaned."],
          a: "He have just cleaned.",
          tip: "HE = HAS (not have)! 'He have' = ERROR"
        },
        {
          type: "match",
          q: "Match subjects to correct HAVE/HAS",
          pairs: [
            { left: "I / You / We / They", right: "have ('ve)" },
            { left: "He / She / It", right: "has ('s)" },
            { left: "My parents", right: "have (they)" }
          ],
          a: "completed"
        }
      ]
    },

    // ===================================================================
    // SET 3: PAST PARTICIPLES - REGULAR (10 preguntas)
    // ===================================================================
    {
      id: "m6-u7-s3",
      title: "Set 3: Regular Past Participles",
      questions: [
        {
          type: "mc",
          q: "REGULAR VERBS: Past participle = base verb + ___",
          options: ["-ed", "-ing", "-s"],
          a: "-ed",
          tip: "REGULAR past participles: clean → cleaned, wash → washed",
          successtip: "✓REGULAR PATTERN!\nBase + ED = past participle ✓\nSame as past simple for regular verbs!"
        },
        {
          type: "order",
          q: "Context: Acabas de limpiar el baño. Traduce: Acabo de limpiar el baño.",
          words: ["I've", "just", "cleaned", "the", "bathroom."],
          a: "I've just cleaned the bathroom.",
          tip: "CLEAN → CLEANED (regular -ed)",
          successtip: "✓BATHROOM!\nBathroom cleaning = common chore 🚽\nClean toilet/sink/mirror/tub"
        },
        {
          type: "input",
          q: "Base: vacuum. Past participle: ___",
          a: "vacuumed",
          accept: { ignoreCase: true, ignorePunctuation: true }
        },
        {
          type: "mc",
          q: "They've just ___ the kitchen counter.",
          options: ["wiped", "wipe", "wiping"],
          a: "wiped",
          tip: "WIPE → WIPED (regular -ed)",
          successtip: "✓WIPE!\n'Wipe the counter' = limpiar la encimera 🧽\nKitchen cleaning vocabulary!"
        },
        {
          type: "order",
          q: "Context: Tu mamá acaba de cocinar la cena. Traduce: Ella acaba de cocinar la cena.",
          words: ["She's", "just", "cooked", "dinner."],
          a: "She's just cooked dinner.",
          tip: "COOK → COOKED (regular -ed)"
        },
        {
          type: "mc",
          q: "We've just ___ the groceries.",
          options: ["unpacked", "unpack", "unpacking"],
          a: "unpacked",
          tip: "UNPACK → UNPACKED (regular -ed)",
          successtip: "✓UNPACK!\n'Unpack groceries' = desempacar las compras 🛒\nAfter shopping!"
        },
        {
          type: "input",
          q: "Complete: I've just ___ the plants. (water)",
          a: "watered",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓WATER THE PLANTS!\n'Water the plants' = regar las plantas 🌱\nRegular household task!"
        },
        {
          type: "order",
          q: "Context: Acabas de planchar tu camisa. Traduce: Acabo de planchar mi camisa.",
          words: ["I've", "just", "ironed", "my", "shirt."],
          a: "I've just ironed my shirt.",
          tip: "IRON → IRONED (regular -ed)",
          successtip: "✓IRON!\n'Iron' = planchar 👔\nIron clothes/shirts/pants"
        },
        {
          type: "match",
          q: "Match base verbs to past participles (all regular)",
          pairs: [
            { left: "clean", right: "cleaned" },
            { left: "mop", right: "mopped" },
            { left: "dust", right: "dusted" }
          ],
          a: "completed",
          successtip: "✓DUST!\n'Dust' = quitar el polvo 🧹\nDust furniture/shelves/tables"
        },
        {
          type: "mc",
          q: "He's just ___ the garbage.",
          options: ["emptied", "empty", "emptying"],
          a: "emptied",
          tip: "EMPTY → EMPTIED (regular -ed, Y becomes IED)",
          successtip: "✓EMPTY!\n'Empty the garbage' = vaciar la basura 🗑️\nSimilar to 'take out the trash'"
        }
      ]
    },

    // ===================================================================
    // SET 4: PAST PARTICIPLES - IRREGULAR (10 preguntas)
    // ===================================================================
    {
      id: "m6-u7-s4",
      title: "Set 4: Irregular Past Participles",
      questions: [
        {
          type: "mc",
          q: "IRREGULAR VERBS: Past participle ≠ base + ed. Example:",
          options: ["do → done (NOT 'doed')", "clean → cleaned", "wash → washed"],
          a: "do → done (NOT 'doed')",
          tip: "IRREGULAR = special forms! Must memorize",
          successtip: "✓IRREGULAR!\nNo rules for irregular verbs 🤷\nMust learn each one!\nCommon in daily English!"
        },
        {
          type: "order",
          q: "Context: Acabas de hacer la compra. Traduce: Acabo de hacer la compra.",
          words: ["I've", "just", "done", "the", "shopping."],
          a: "I've just done the shopping.",
          tip: "DO → DONE (irregular!)",
          successtip: "✓DO THE SHOPPING!\n'Do the shopping' = hacer la compra 🛒\nGrocery shopping!"
        },
        {
          type: "input",
          q: "Base: make. Irregular past participle: ___",
          a: "made",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "MAKE → MADE (NOT 'maked')"
        },
        {
          type: "mc",
          q: "She's just ___ lunch for everyone.",
          options: ["made", "maked", "make"],
          a: "made",
          tip: "MAKE → MADE (irregular)",
          successtip: "✓MAKE LUNCH!\nMake breakfast/lunch/dinner 🍽️\nPrepare meals vocabulary!"
        },
        {
          type: "order",
          q: "Context: Tu papá acaba de cortar el césped. Traduce: Él acaba de cortar el césped.",
          words: ["He's", "just", "cut", "the", "grass."],
          a: "He's just cut the grass.",
          tip: "CUT → CUT (same form! irregular)",
          successtip: "✓CUT THE GRASS!\n'Cut/Mow the grass' = cortar el césped 🌿\nOutdoor chore!"
        },
        {
          type: "mc",
          q: "We've just ___ the dog.",
          options: ["fed", "feed", "feeded"],
          a: "fed",
          tip: "FEED → FED (irregular)",
          successtip: "✓FEED!\n'Feed the dog/cat/pets' = alimentar las mascotas 🐕\nDaily pet care!"
        },
        {
          type: "input",
          q: "Base: put away (the clothes). Past participle: ___ ___",
          a: "put away",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "PUT → PUT (same form! irregular)",
          successtip: "✓PUT AWAY!\n'Put away' = guardar 📦\nPut away clothes/dishes/toys"
        },
        {
          type: "order",
          q: "Context: Acabas de colgar la ropa. Traduce: Acabo de colgar la ropa.",
          words: ["I've", "just", "hung", "up", "the", "laundry."],
          a: "I've just hung up the laundry.",
          tip: "HANG → HUNG (irregular)",
          successtip: "✓HANG UP!\n'Hang up the laundry' = colgar la ropa 👕\nAfter washing!"
        },
        {
          type: "match",
          q: "Match irregular base verbs to past participles",
          pairs: [
            { left: "do", right: "done" },
            { left: "make", right: "made" },
            { left: "take (out)", right: "taken (out)" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "ERROR: Which is WRONG?",
          options: ["I've just maked breakfast.", "I've just made breakfast.", "I've just done the dishes."],
          a: "I've just maked breakfast.",
          tip: "'MAKED' is not a word! MAKE → MADE (irregular)",
          successtip: "✓COMMON ERROR!\nMaked ❌ WRONG!\nMade ✓ CORRECT!\nIrregular verbs = tricky for learners!"
        }
      ]
    },

    // ===================================================================
    // SET 5: PRESENT PERFECT QUESTIONS (10 preguntas)
    // ===================================================================
    {
      id: "m6-u7-s5",
      title: "Set 5: Asking Questions",
      questions: [
        {
          type: "mc",
          q: "QUESTION FORM: ___ you cleaned your room?",
          options: ["Have", "Has", "Did"],
          a: "Have",
          tip: "QUESTION: Have/Has + subject + past participle?",
          successtip: "✓QUESTIONS!\nHave you...? (I/you/we/they) ✓\nHas he/she/it...? ✓\nNo 'did' in Present Perfect!"
        },
        {
          type: "order",
          q: "Context: Preguntas si tu hermano lavó los platos. Traduce: ¿Has lavado los platos?",
          words: ["Have", "you", "washed", "the", "dishes?"],
          a: "Have you washed the dishes?",
          tip: "HAVE + YOU + past participle"
        },
        {
          type: "input",
          q: "___ she finished the laundry? (question)",
          a: "Has",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "SHE = HAS (not have)"
        },
        {
          type: "mc",
          q: "'Has he taken out the trash?' Short answer YES:",
          options: ["Yes, he has.", "Yes, he have.", "Yes, he did."],
          a: "Yes, he has.",
          tip: "SHORT ANSWER: Yes/No + subject + have/has",
          successtip: "✓SHORT ANSWERS!\nYes, I have / No, I haven't ✓\nYes, he has / No, he hasn't ✓\nVery common in conversation!"
        },
        {
          type: "order",
          q: "Context: Preguntas si ellos limpiaron el baño. Traduce: ¿Han limpiado el baño?",
          words: ["Have", "they", "cleaned", "the", "bathroom?"],
          a: "Have they cleaned the bathroom?"
        },
        {
          type: "mc",
          q: "'Have you made the bed?' Short answer NO:",
          options: ["No, I haven't.", "No, I not have.", "No, I didn't."],
          a: "No, I haven't.",
          tip: "NEGATIVE SHORT: No + subject + haven't/hasn't"
        },
        {
          type: "input",
          q: "Question: ___ we finished all the chores? (have/has)",
          a: "Have",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "WE = HAVE"
        },
        {
          type: "order",
          q: "Context: Preguntas si tu mamá regó las plantas. Traduce: ¿Ha regado las plantas?",
          words: ["Has", "she", "watered", "the", "plants?"],
          a: "Has she watered the plants?",
          tip: "HAS + SHE + past participle"
        },
        {
          type: "mc",
          q: "ERROR: Which question is WRONG?",
          options: ["Did you have cleaned?", "Have you cleaned?", "Has he cleaned?"],
          a: "Did you have cleaned?",
          tip: "NEVER 'did + have'! Present Perfect = have/has (NOT did)",
          successtip: "✓COMMON ERROR!\nDid + have ❌ WRONG!\nHave/Has alone ✓ CORRECT!\nDon't mix present perfect with past simple!"
        },
        {
          type: "match",
          q: "Match questions to short answers",
          pairs: [
            { left: "Have you cleaned?", right: "Yes, I have." },
            { left: "Has she finished?", right: "No, she hasn't." },
            { left: "Have they done it?", right: "Yes, they have." }
          ],
          a: "completed"
        }
      ]
    },

    // ===================================================================
    // SET 6: NEGATIVE PRESENT PERFECT (10 preguntas)
    // ===================================================================
    {
      id: "m6-u7-s6",
      title: "Set 6: Negative Forms",
      questions: [
        {
          type: "mc",
          q: "NEGATIVE: I ___ cleaned my room yet.",
          options: ["haven't", "hasn't", "didn't"],
          a: "haven't",
          tip: "NEGATIVE: haven't/hasn't + past participle",
          successtip: "✓HAVEN'T!\nHaven't = have not ✓\nHasn't = has not ✓\nContractions very common!"
        },
        {
          type: "order",
          q: "Context: No has hecho la cama todavía. Traduce: No he hecho la cama todavía.",
          words: ["I", "haven't", "made", "the", "bed", "yet."],
          a: "I haven't made the bed yet.",
          tip: "HAVEN'T + past participle. YET = todavía (negative)",
          successtip: "✓YET!\n'Yet' = todavía (con negativos) ⏰\nGoes at the end!\nI haven't done it yet = no lo he hecho todavía"
        },
        {
          type: "input",
          q: "She ___ finished the dishes. (hasn't/haven't)",
          a: "hasn't",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "SHE = HASN'T (not haven't)"
        },
        {
          type: "mc",
          q: "They ___ taken out the trash yet.",
          options: ["haven't", "hasn't", "didn't"],
          a: "haven't",
          tip: "THEY = HAVEN'T (not hasn't)"
        },
        {
          type: "order",
          q: "Context: Él no ha aspirado todavía. Traduce: Él no ha aspirado todavía.",
          words: ["He", "hasn't", "vacuumed", "yet."],
          a: "He hasn't vacuumed yet.",
          tip: "HASN'T + past participle + YET"
        },
        {
          type: "mc",
          q: "We ___ cleaned the windows. They're still dirty!",
          options: ["haven't", "hasn't", "didn't"],
          a: "haven't",
          tip: "WE = HAVEN'T. Still dirty = result in present!"
        },
        {
          type: "input",
          q: "My roommate ___ done his chores. (hasn't)",
          a: "hasn't",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓CHORES!\n'Chores' = quehaceres 🧹\nPlural = multiple tasks"
        },
        {
          type: "order",
          q: "Context: No hemos sacado la basura todavía. Traduce: No hemos sacado la basura todavía.",
          words: ["We", "haven't", "taken", "out", "the", "trash", "yet."],
          a: "We haven't taken out the trash yet."
        },
        {
          type: "mc",
          q: "ERROR: Which is WRONG?",
          options: ["He haven't cleaned.", "He hasn't cleaned.", "They haven't cleaned."],
          a: "He haven't cleaned.",
          tip: "HE = HASN'T (not haven't)! Subject-verb agreement"
        },
        {
          type: "match",
          q: "Match subjects to correct negative forms",
          pairs: [
            { left: "I / You / We / They", right: "haven't" },
            { left: "He / She / It", right: "hasn't" },
            { left: "My sister", right: "hasn't (she)" }
          ],
          a: "completed"
        }
      ]
    },

    // ===================================================================
    // SET 7: MORE HOUSEHOLD VOCABULARY (10 preguntas)
    // ===================================================================
    {
      id: "m6-u7-s7",
      title: "Set 7: More Chores Vocabulary",
      questions: [
        {
          type: "mc",
          q: "Kitchen chore: After eating, you need to...",
          options: ["clear the table", "make the table", "clean the table"],
          a: "clear the table",
          tip: "'CLEAR THE TABLE' = remove dishes after eating",
          successtip: "✓CLEAR THE TABLE!\n'Clear' = quitar cosas de 🍽️\nAfter-meal chore!\nThen wash dishes!"
        },
        {
          type: "order",
          q: "Context: Acabas de lavar y secar los platos. Traduce: Acabo de lavar y secar los platos.",
          words: ["I've", "just", "washed", "and", "dried", "the", "dishes."],
          a: "I've just washed and dried the dishes.",
          successtip: "✓DRY!\n'Dry the dishes' = secar los platos 🧻\nAfter washing!"
        },
        {
          type: "input",
          q: "You've just ___ the table for dinner. (set = poner la mesa)",
          a: "set",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓SET THE TABLE!\n'Set the table' = poner la mesa 🍴\nBefore eating!\nSET → SET (irregular, same form)"
        },
        {
          type: "mc",
          q: "Bedroom chore: organizing clothes in drawers/closet",
          options: ["I've put away my clothes.", "I've put on my clothes.", "I've taken off my clothes."],
          a: "I've put away my clothes.",
          tip: "'PUT AWAY' = guardar (organizing)",
          successtip: "✓PUT AWAY vs PUT ON!\nPut away = guardar 📦\nPut on = ponerse (wear) 👕\nDifferent meanings!"
        },
        {
          type: "order",
          q: "Context: Acabas de doblar la ropa limpia. Traduce: Acabo de doblar la ropa.",
          words: ["I've", "just", "folded", "the", "laundry."],
          a: "I've just folded the laundry.",
          successtip: "✓FOLD!\n'Fold' = doblar 👔\nFold clothes/laundry/towels\nAfter drying!"
        },
        {
          type: "mc",
          q: "Bathroom chore: cleaning the toilet/sink/tub",
          options: ["I've scrubbed the bathroom.", "I've scratched the bathroom.", "I've scrabbled the bathroom."],
          a: "I've scrubbed the bathroom.",
          tip: "'SCRUB' = limpiar restregando (deep cleaning)",
          successtip: "✓SCRUB!\n'Scrub' = restregar/tallar 🧽\nDeep cleaning verb!\nScrub the toilet/tub/sink"
        },
        {
          type: "input",
          q: "Outdoor chore: We've just ___ the lawn. (mow = cortar césped)",
          a: "mowed",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["mown"] },
          successtip: "✓MOW THE LAWN!\n'Mow the lawn' = cortar el césped 🌿\nMOW → MOWED/MOWN (both OK)\nOutdoor chore!"
        },
        {
          type: "order",
          q: "Context: Acabas de pasar el plumero por los muebles. Traduce: Acabo de quitar el polvo de los muebles.",
          words: ["I've", "just", "dusted", "the", "furniture."],
          a: "I've just dusted the furniture.",
          successtip: "✓FURNITURE!\n'Furniture' = muebles 🛋️\nUncountable noun!\nDust the furniture/shelves"
        },
        {
          type: "mc",
          q: "Kitchen appliance cleaning: loading and running it",
          options: ["I've loaded the dishwasher.", "I've uploaded the dishwasher.", "I've downloaded the dishwasher."],
          a: "I've loaded the dishwasher.",
          successtip: "✓LOAD THE DISHWASHER!\n'Load' = cargar 🍽️\nLoad the dishwasher/washing machine\nOpposite: unload"
        },
        {
          type: "match",
          q: "Match chores to vocabulary",
          pairs: [
            { left: "After eating", right: "clear the table" },
            { left: "Before eating", right: "set the table" },
            { left: "Organize clothes", right: "put away" }
          ],
          a: "completed",
          successtip: "✓MEAL-TIME CHORES!\nSet (before) → Eat → Clear (after) 🍽️\nDaily routine!"
        }
      ]
    },

    // ===================================================================
    // SET 8: PRESENT PERFECT vs PAST SIMPLE (10 preguntas)
    // ===================================================================
    {
      id: "m6-u7-s8",
      title: "Set 8: Present Perfect vs Past Simple",
      questions: [
        {
          type: "mc",
          q: "CONTRAST: Which shows action JUST happened (connected to now)?",
          options: ["I've just cleaned the kitchen.", "I cleaned the kitchen.", "I'm cleaning the kitchen."],
          a: "I've just cleaned the kitchen.",
          tip: "PRESENT PERFECT = connected to now! PAST SIMPLE = finished past",
          successtip: "✓KEY DIFFERENCE!\nPresent Perfect = result visible NOW 🎯\nPast Simple = finished time in past 📅\nCrucial distinction!"
        },
        {
          type: "mc",
          q: "FINISHED PAST with time: 'I ___ the dishes yesterday.'",
          options: ["washed", "have washed", "'ve washed"],
          a: "washed",
          tip: "YESTERDAY = specific past time → use PAST SIMPLE (not perfect)",
          successtip: "✓TIME MARKERS!\nYesterday/last week/in 2020 = PAST SIMPLE ✓\nJust/recently/already = PRESENT PERFECT ✓\nTime word helps you choose!"
        },
        {
          type: "order",
          q: "Context: Acabas de terminar (justo ahora). Traduce: Acabo de terminar.",
          words: ["I've", "just", "finished."],
          a: "I've just finished.",
          tip: "JUST = recently → PRESENT PERFECT"
        },
        {
          type: "mc",
          q: "PAST SIMPLE: 'He ___ the laundry this morning.' (morning is over)",
          options: ["did", "has done", "'s done"],
          a: "did",
          tip: "THIS MORNING (finished) = PAST SIMPLE if morning is over!",
          successtip: "✓THIS MORNING!\nMorning finished → PAST SIMPLE ✓\nMorning still ongoing → could use PERFECT ✓\nTime frame matters!"
        },
        {
          type: "input",
          q: "PRESENT PERFECT: I ___ already vacuumed. (have/had)",
          a: "have",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["'ve"] },
          tip: "ALREADY = present perfect (result: it's done now)",
          successtip: "✓ALREADY!\n'Already' = ya ✓\nI've already done it = ya lo hice\nResult exists now!"
        },
        {
          type: "order",
          q: "Context: Pasado terminado - ayer. Traduce: Limpié mi cuarto ayer.",
          words: ["I", "cleaned", "my", "room", "yesterday."],
          a: "I cleaned my room yesterday.",
          tip: "YESTERDAY = specific time → PAST SIMPLE (NOT perfect)"
        },
        {
          type: "mc",
          q: "Which can use PRESENT PERFECT?",
          options: ["I've just made the bed. (just now)", "I made the bed last week.", "I made the bed in 2020."],
          a: "I've just made the bed. (just now)",
          tip: "RECENT action with JUST = PRESENT PERFECT fits!"
        },
        {
          type: "match",
          q: "Match time expressions to tenses",
          pairs: [
            { left: "just / recently / already", right: "Present Perfect" },
            { left: "yesterday / last week / ago", right: "Past Simple" },
            { left: "Result visible now", right: "Present Perfect" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "MOM ASKS: 'Have you done your homework?' (wants to know NOW):",
          options: ["Yes, I've just finished it!", "Yes, I finished it last week.", "Yes, I finish it."],
          a: "Yes, I've just finished it!",
          tip: "Asking about NOW = PRESENT PERFECT fits best!",
          successtip: "✓HOMEWORK!\nParents love this question! 📚\nHave you done...? = checking current status"
        },
        {
          type: "order",
          q: "Context: Resultado presente (la cocina está limpia ahora). Traduce: He limpiado la cocina.",
          words: ["I've", "cleaned", "the", "kitchen."],
          a: "I've cleaned the kitchen.",
          tip: "RESULT NOW = PRESENT PERFECT (kitchen is clean)"
        }
      ]
    },

    // ===================================================================
    // SET 9: INTEGRATION - COMPLETE SENTENCES (10 preguntas)
    // ===================================================================
    {
      id: "m6-u7-s9",
      title: "Set 9: Full Conversations",
      questions: [
        {
          type: "mc",
          q: "'Is the kitchen clean?' 'Yes, ___'",
          options: ["I've just cleaned it.", "I cleaned it.", "I clean it."],
          a: "I've just cleaned it.",
          tip: "IS IT CLEAN? (now) → Yes, PRESENT PERFECT (I've just...)",
          successtip: "✓NATURAL RESPONSE!\nQuestion about NOW → Perfect tense answer ✓\nVery common conversation pattern!"
        },
        {
          type: "order",
          q: "Context: Roommate pregunta si hiciste tus tareas. Traduce: Sí, acabo de terminarlas.",
          words: ["Yes,", "I've", "just", "finished", "them."],
          a: "Yes, I've just finished them.",
          tip: "THEM = las tareas (chores). JUST finished = present perfect"
        },
        {
          type: "input",
          q: "'Has anyone fed the cat?' 'Yes, I ___ already fed her.'",
          a: "have",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["'ve"] },
          successtip: "✓FED!\nFEED → FED (irregular) 🐱\nAlready = ya lo hice"
        },
        {
          type: "mc",
          q: "'The bathroom smells nice!' Natural response:",
          options: ["I've just scrubbed it!", "I scrubbed it last month.", "I scrub it."],
          a: "I've just scrubbed it!",
          tip: "SMELLS NICE NOW = result of recent action (present perfect)",
          successtip: "✓RESULT-BASED!\nPresent result = present perfect explains it ✓\nVery logical!"
        },
        {
          type: "order",
          q: "Context: Todo está limpio ahora. Traduce: Hemos terminado toda la limpieza.",
          words: ["We've", "finished", "all", "the", "cleaning."],
          a: "We've finished all the cleaning.",
          successtip: "✓CLEANING!\n'Cleaning' = limpieza 🧹\nGeneral term for all cleaning tasks"
        },
        {
          type: "mc",
          q: "'Can I use the dishwasher?' 'Not yet, ___'",
          options: ["I haven't unloaded it.", "I didn't unload it.", "I'm not unloading it."],
          a: "I haven't unloaded it.",
          tip: "YET = todavía no → HAVEN'T + past participle",
          successtip: "✓UNLOAD!\n'Unload the dishwasher' = sacar los platos limpios 🍽️\nOpposite of load!"
        },
        {
          type: "input",
          q: "'Why are your hands wet?' 'I ___ just washed the dishes.'",
          a: "'ve",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["have", "'ve"] },
          tip: "HANDS WET NOW = evidence of recent action!"
        },
        {
          type: "order",
          q: "Context: Tu mamá pregunta por la basura. Traduce: Ya la saqué.",
          words: ["I've", "already", "taken", "it", "out."],
          a: "I've already taken it out.",
          tip: "ALREADY = ya (present perfect). IT = the trash"
        },
        {
          type: "mc",
          q: "CHORE LIST: 'What's left to do?' '___'",
          options: ["We haven't mopped the floor yet.", "We didn't mop the floor.", "We don't mop the floor."],
          a: "We haven't mopped the floor yet.",
          tip: "WHAT'S LEFT? = what still needs doing → HAVEN'T...YET",
          successtip: "✓CHORE LISTS!\nHaven't...yet = still needs doing ⏰\nCommon household conversation!"
        },
        {
          type: "match",
          q: "Match questions to natural responses",
          pairs: [
            { left: "Have you cleaned?", right: "Yes, I've just finished" },
            { left: "Is dinner ready?", right: "I've just made it" },
            { left: "Did anyone water plants?", right: "I've already done it" }
          ],
          a: "completed"
        }
      ]
    },

    // ===================================================================
    // SET 10: MASTERY - HOUSEHOLD CONVERSATIONS (10 preguntas)
    // ===================================================================
    {
      id: "m6-u7-s10",
      title: "Set 10: Household Mastery",
      questions: [
        {
          type: "mc",
          q: "NATURAL: Parent arrives home and sees clean house. They say:",
          options: ["Wow! You've cleaned the whole house!", "Wow! You cleaned the whole house!", "Wow! You're cleaning!"],
          a: "Wow! You've cleaned the whole house!",
          tip: "SEE RESULT NOW → PRESENT PERFECT most natural!",
          successtip: "✓IMPRESSED PARENT!\nSeeing result = present perfect reaction 😊\nVery natural response!"
        },
        {
          type: "mc",
          q: "ROOMMATE CONVERSATION: 'Whose turn is it to clean?' '___'",
          options: ["I've already done my part.", "I already did my part yesterday.", "I do my part."],
          a: "I've already done my part.",
          tip: "DEFENDING YOURSELF = present perfect (I've done it!)",
          successtip: "✓ROOMMATE DYNAMICS!\n'I've already done it' = common defense 🛡️\nShowing you're not shirking!"
        },
        {
          type: "order",
          q: "Context: Mostrando que completaste todo de tu lista. Traduce: He hecho todo lo de mi lista.",
          words: ["I've", "done", "everything", "on", "my", "list."],
          a: "I've done everything on my list.",
          successtip: "✓EVERYTHING!\n'Everything on my list' = todo de mi lista ✅\nCompleting chores!"
        },
        {
          type: "mc",
          q: "OFFERING HELP: 'I can help!' Response: 'Thanks, but ___'",
          options: ["I've already finished it.", "I already finish it.", "I'm finishing it."],
          a: "I've already finished it.",
          tip: "ALREADY DONE = present perfect (no help needed now)",
          successtip: "✓POLITE DECLINE!\n'I've already done it' = thanks but done ✓\nPolite way to decline help!"
        },
        {
          type: "input",
          q: "CHECKING CHORES: 'Have you tidied up?' 'Yes, I ___ just finished.'",
          a: "'ve",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["have", "'ve"] }
        },
        {
          type: "mc",
          q: "INSPECTION: Mom checks and finds dust. '___'",
          options: ["You haven't dusted the shelves.", "You didn't dust the shelves yesterday.", "You're not dusting."],
          a: "You haven't dusted the shelves.",
          tip: "FINDING PROBLEM NOW → PRESENT PERFECT negative",
          successtip: "✓BUSTED!\nHaven't + past participle = caught not doing it 😅\nParent detection!"
        },
        {
          type: "order",
          q: "Context: Defendiéndote - ya lo hiciste. Traduce: Pero ya hice mi cama.",
          words: ["But", "I've", "already", "made", "my", "bed."],
          a: "But I've already made my bed.",
          successtip: "✓DEFENSE!\n'But I've already...' = common defense 🛡️\nShowing you did it!"
        },
        {
          type: "mc",
          q: "DAILY ROUTINE: Every morning after waking up, first you:",
          options: ["make the bed", "do the laundry", "mow the lawn"],
          a: "make the bed",
          tip: "MORNING ROUTINE: make bed first!",
          successtip: "✓MORNING CHORES!\nMake bed = first daily chore 🛏️\nGood habit!\nThen breakfast, then other chores"
        },
        {
          type: "match",
          q: "Match household conversation contexts",
          pairs: [
            { left: "Showing completion", right: "I've done it" },
            { left: "Checking status", right: "Have you finished?" },
            { left: "Finding problem", right: "You haven't cleaned" }
          ],
          a: "completed"
        },
        {
          type: "mc",
          q: "FINAL MASTERY: Most natural household exchange:",
          options: ["'Is the kitchen clean?' - 'Yes, I've just cleaned it!'", "'Is the kitchen clean?' - 'Yes, I cleaned it last week.'", "'Is the kitchen clean?' - 'Yes, I'm cleaning it.'"],
          a: "'Is the kitchen clean?' - 'Yes, I've just cleaned it!'",
          successtip: "✓UNIT 7 COMPLETE! 🎉🏆\n\nYOU MASTERED:\n✓ PRESENT PERFECT structure\n  • I/You/We/They + have ('ve)\n  • He/She/It + has ('s)\n  • + past participle\n✓ JUST for recent actions\n✓ ALREADY for completed actions\n✓ YET for pending actions (negative/question)\n✓ Regular past participles (-ed)\n✓ 30+ irregular past participles:\n  • do → done\n  • make → made\n  • take → taken\n  • feed → fed\n  • hang → hung\n  • cut → cut\n  • put → put\n  • And more!\n✓ Questions (Have you...?)\n✓ Negatives (haven't/hasn't)\n✓ Short answers\n✓ Present Perfect vs Past Simple\n✓ 40+ HOUSEHOLD VOCABULARY:\n  • Kitchen: wash/dry dishes, clear/set table, load/unload dishwasher\n  • Cleaning: vacuum, mop, sweep, dust, wipe, scrub\n  • Laundry: do laundry, fold, hang up, put away, iron\n  • Bedroom: make bed, tidy up\n  • Bathroom: clean, scrub\n  • Outdoor: mow lawn, cut grass, water plants\n  • Trash: take out, empty\n  • General: do chores, do housework\n  • And many more!\n\nYou can now:\n🏠 Talk about household chores\n🎯 Describe recent actions\n✅ Show task completion\n❓ Ask about chores\n🗣️ Have natural household conversations\n📚 Use 40+ chore vocabulary items\n⏰ Use time markers (just/already/yet)\n🔄 Switch between Perfect and Simple\n\n¡Increíble trabajo! Ready for Unit 8! 🚀"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET - PROGRESSIVE FORMAT (10 preguntas en bloques)
  // ===================================================================
  readingSet: {
    id: "m6-u7-reading",
    title: "Reading Practice - Saturday Cleaning Day",
    category: "reading",
    
    questions: [
      // BLOQUE 1: Morning (preguntas 1-2)
      {
        type: "passage",
        passage: `**Saturday Morning - 9:00 AM**

**Text from Mom to Family Group:**

Good morning everyone! Today is cleaning day. Here's what needs to be done:

KITCHEN:
- Wash and dry the dishes
- Wipe the counters
- Mop the floor
- Empty the trash

BATHROOMS:
- Scrub the toilet and sink
- Clean the mirrors
- Mop the floor

BEDROOMS:
- Make your beds
- Vacuum the carpets
- Dust the furniture

LIVING ROOM:
- Vacuum the carpet
- Dust the shelves
- Tidy up

Let's get it all done by lunchtime! Dad and I will handle the bathrooms. Kids - you know what to do!`
      },
      {
        type: "mc",
        q: "What tense is Mom using for the chore list?",
        options: ["Imperatives/commands (base verbs)", "Present Perfect", "Past Simple"],
        a: "Imperatives/commands (base verbs)",
        tip: "Chore lists use commands: Wash, clean, mop"
      },
      {
        type: "mc",
        q: "How many rooms need cleaning?",
        options: ["Four (kitchen, bathrooms, bedrooms, living room)", "Two", "Three"],
        a: "Four (kitchen, bathrooms, bedrooms, living room)"
      },

      // BLOQUE 2: Mid-morning Updates (preguntas 3-4)
      {
        type: "passage",
        passage: `**10:30 AM - Progress Updates**

**Sarah (daughter):**
I've just finished my bedroom! I've made my bed, vacuumed the carpet, and dusted all the furniture. The room looks great! 😊

Should I start on the living room now?

**Jake (son):**
I've done the kitchen dishes and wiped the counters, but I haven't mopped the floor yet. Taking a quick break.

**Mom:**
Great work, Sarah! Yes, please start on the living room. Jake - don't take too long! We need to finish before lunch.

**Dad:**
I've already scrubbed both bathrooms and cleaned the mirrors. Just need to mop the bathroom floors now.`
      },
      {
        type: "mc",
        q: "Sarah uses 'I've just finished' because:",
        options: ["She finished very recently (present perfect)", "She finished yesterday", "She's still doing it"],
        a: "She finished very recently (present perfect)",
        successtip: "✓JUST!\nSarah shows recent completion ✅"
      },
      {
        type: "mc",
        q: "What hasn't Jake done yet?",
        options: ["Mopped the floor", "Washed the dishes", "Wiped the counters"],
        a: "Mopped the floor",
        tip: "HAVEN'T...YET = still pending"
      },

      // BLOQUE 3: Near Completion (preguntas 5-7)
      {
        type: "passage",
        passage: `**11:45 AM - Almost Done**

**Sarah:**
Living room is done! I've vacuumed, dusted the shelves, and tidied up all the magazines and remotes. Everything looks perfect!

**Jake:**
Finally finished the kitchen! I've mopped the floor and taken out the trash. Sorry it took so long - I got distracted by my phone 😅

**Mom:**
Has anyone fed the dog? He's been staring at his empty bowl for 10 minutes!

**Dad:**
I haven't fed him yet - I've been busy with the bathrooms. Sarah, can you do it?

**Sarah:**
Already done! I fed him while the vacuum was charging.

**Mom:**
Perfect! So let me check... we've cleaned all the bathrooms, the kitchen is done, all bedrooms are clean, and the living room is finished. Have we done everything?`
      },
      {
        type: "mc",
        q: "Why did Jake's kitchen take longer?",
        options: ["He got distracted by his phone", "He did extra chores", "He helped someone else"],
        a: "He got distracted by his phone",
        successtip: "✓DISTRACTED!\nCommon teenager problem 📱😅"
      },
      {
        type: "mc",
        q: "When did Sarah feed the dog?",
        options: ["While the vacuum was charging (already done)", "She hasn't done it yet", "Dad did it"],
        a: "While the vacuum was charging (already done)",
        tip: "ALREADY = ya lo hizo"
      },
      {
        type: "mc",
        q: "Dad says 'I haven't fed him yet' - what does this mean?",
        options: ["He didn't do it (still pending)", "He did it earlier", "He's doing it now"],
        a: "He didn't do it (still pending)",
        successtip: "✓HAVEN'T...YET!\nStill not done = pending task ⏰"
      },

      // BLOQUE 4: Final Check (preguntas 8-9)
      {
        type: "passage",
        passage: `**12:15 PM - Final Inspection**

**Mom (doing final walk-through):**
Hmm... Jake, you haven't emptied the trash in the living room. I can see it's full!

**Jake:**
Oops! I only emptied the kitchen trash. I'll do it right now!

**Mom:**
And Sarah, have you put away your clean laundry? I saw it on your bed.

**Sarah:**
Not yet, but I'll fold it and put it away after lunch, I promise!

**Dad:**
I've just finished mopping the last bathroom. That's everything on my list!

**Mom:**
Excellent! Once Jake empties that trash and we put away the cleaning supplies, we're done. Great teamwork, everyone! Who wants pizza for lunch?`
      },
      {
        type: "mc",
        q: "What did Jake forget?",
        options: ["Emptying the living room trash", "Mopping the kitchen", "Vacuuming"],
        a: "Emptying the living room trash",
        tip: "Mom caught him! YOU HAVEN'T emptied..."
      },
      {
        type: "mc",
        q: "Sarah promises to put away laundry:",
        options: ["After lunch (future)", "She's already done it", "She did it this morning"],
        a: "After lunch (future)",
        tip: "NOT YET, BUT I'LL... = future promise"
      },

      // PREGUNTA FINAL (10)
      {
        type: "mc",
        q: "Throughout the texts, Present Perfect is used to:",
        options: ["Show what's been completed with visible results NOW", "Talk about yesterday's chores", "Give commands"],
        a: "Show what's been completed with visible results NOW",
        successtip: "✓READING COMPLETE! 🎉\n\nPresent Perfect patterns seen:\n✓ I've just finished (recent)\n✓ I've already done it (completed)\n✓ I haven't done it yet (pending)\n✓ Have you...? (checking)\n\nHousehold vocabulary found:\n🏠 Kitchen: wash/dry dishes, wipe counters, mop floor, empty trash\n🛁 Bathroom: scrub toilet/sink, clean mirrors, mop\n🛏️ Bedroom: make bed, vacuum, dust furniture\n🛋️ Living room: vacuum, dust shelves, tidy up\n🐕 Other: feed dog, take out trash, put away\n\nConversation patterns:\n✅ Showing completion: I've done it\n❓ Checking status: Have you...?\n⏰ Pending tasks: Haven't...yet\n🎯 Recent actions: Just finished\n\nFamily dynamics:\n👩 Mom = organizer/inspector\n👨 Dad = reliable helper\n👧 Sarah = efficient worker\n👦 Jake = easily distracted\n\nYou understood Present Perfect in real household context! 🌟\nThis is how families really talk about chores!"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (10 preguntas)
  // ===================================================================
  reviewSet: {
    id: "m6-u7-review",
    title: "Unit Review - Present Perfect Mastery",
    category: "review",
    exercises: [
      {
        type: "mc",
        q: "REVIEW: Present Perfect form = ___",
        options: ["have/has + past participle", "did + base verb", "am/is/are + verb-ing"],
        a: "have/has + past participle",
        tip: "HAVE/HAS + PAST PARTICIPLE"
      },
      {
        type: "mc",
        q: "REVIEW: I/You/We/They = ___ / He/She/It = ___",
        options: ["have / has", "has / have", "have / have"],
        a: "have / has",
        successtip: "✓FORMS MASTERED!"
      },
      {
        type: "order",
        q: "REVIEW: Traduce: Acabo de limpiar mi cuarto.",
        words: ["I've", "just", "cleaned", "my", "room."],
        a: "I've just cleaned my room.",
        tip: "JUST = acabo de"
      },
      {
        type: "mc",
        q: "REVIEW: Regular past participle of 'clean':",
        options: ["cleaned", "clean", "cleaning"],
        a: "cleaned",
        successtip: "✓REGULAR!\nBase + ED"
      },
      {
        type: "mc",
        q: "REVIEW: Irregular past participle of 'make':",
        options: ["made", "maked", "make"],
        a: "made",
        successtip: "✓IRREGULAR!\nMake → made ✓"
      },
      {
        type: "input",
        q: "REVIEW: Question form: ___ you finished? (Have/Has)",
        a: "Have",
        accept: { ignoreCase: true, ignorePunctuation: true },
        tip: "YOU = HAVE"
      },
      {
        type: "mc",
        q: "REVIEW: Negative: He ___ cleaned yet.",
        options: ["hasn't", "haven't", "didn't"],
        a: "hasn't",
        successtip: "✓NEGATIVE!\nHasn't + past participle"
      },
      {
        type: "mc",
        q: "REVIEW: Time marker with Present Perfect:",
        options: ["just / already / yet", "yesterday / last week / ago", "tomorrow / next week"],
        a: "just / already / yet",
        successtip: "✓TIME MARKERS!\nJust/already/yet = Perfect ✓\nYesterday/ago = Simple ✓"
      },
      {
        type: "match",
        q: "REVIEW: Match household chores",
        pairs: [
          { left: "Kitchen", right: "wash the dishes" },
          { left: "Bedroom", right: "make the bed" },
          { left: "Floor", right: "vacuum the carpet" }
        ],
        a: "completed",
        successtip: "✓40+ CHORES!\nYou learned massive vocabulary! 🏠"
      },
      {
        type: "mc",
        q: "FINAL REVIEW: 'Have you done the laundry?' - Natural answer:",
        options: ["Yes, I've just finished it.", "Yes, I finished it last week.", "Yes, I'm finishing it."],
        a: "Yes, I've just finished it.",
        successtip: "✓UNIT 7 MASTERY COMPLETE! 🎉🏆\n\nYOU ACHIEVED:\n✓ PRESENT PERFECT structure\n✓ HAVE/HAS forms\n✓ Regular past participles\n✓ 30+ irregular past participles\n✓ JUST/ALREADY/YET\n✓ Questions & negatives\n✓ Present Perfect vs Past Simple\n✓ 40+ HOUSEHOLD VOCABULARY:\n  Kitchen, bathroom, bedroom, living room, laundry, outdoor chores, pet care, and more!\n\nYou can now:\n🏠 Talk about all household chores\n✅ Show task completion\n🎯 Describe recent actions\n❓ Ask about chores naturally\n🗣️ Have household conversations\n📚 Use massive chore vocabulary\n⏰ Use time markers correctly\n🔄 Choose Perfect vs Simple\n\n¡Excelente progreso! Ready for Unit 8! 🚀"
      }
    ]
  }
};
